"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PaymentReconcileRequest = exports.PaymentAttemptSummary = exports.AcquirerBatchUploadResponse = exports.AcquirerBatchDiscrepancy = exports.AcquirerBatchUploadRequest = exports.AcquirerBatchRecord = exports.BatchReport = exports.ReversalRequest = exports.PaymentResult = exports.PaymentAttempt = exports.PaymentAttemptId = exports.PaymentMethod = exports.PaymentState = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
/**
 * Payment contracts (Sprint 6 — BOS-side first half; terminals-side
 * adapter implementation blocked on vendor selection per the
 * autonomous-run plan).
 *
 * The payment state machine (TRS §17.2) is authored by the terminal
 * during a tender flow; BOS keeps the authoritative ledger so the
 * Reconciliation Queue (TRS §17.5) can surface stuck attempts to
 * managers. Idempotency key: client-side ULID per attempt.
 *
 * Sprint 6 BOS scope:
 *  - `PaymentAttempt` envelope + state enum.
 *  - `PaymentResult` (terminal-uploaded ack of the vendor's reply).
 *  - `ReversalRequest` (BOS-authored when a fiscal void / refund
 *    must follow a payment that landed but the receipt failed).
 *  - `BatchReport` (acquirer end-of-day batch close — Sprint 12
 *    fully wires this; the contract ships now).
 *
 * Sprint 7+ adds the terminal-side `PaymentAdapter` implementation
 * + the real vendor SDK bridge.
 */
// ---------- Primitive shapes ----------
exports.PaymentState = zod_1.z.enum([
    'initiated',
    'awaiting_response',
    'approved',
    'declined',
    'indeterminate',
    'voided',
    'reversed',
    'failed',
]);
exports.PaymentMethod = zod_1.z.enum(['card', 'cash', 'voucher', 'split']);
/**
 * ULID-shape idempotency key generated client-side. Crockford-base32
 * 26-char string. Server dedupes on this; replaying an attempt is
 * always safe.
 */
exports.PaymentAttemptId = zod_1.z
    .string()
    .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'ULID-shaped 26-char Crockford base32');
// ---------- PaymentAttempt — opens the lifecycle ----------
exports.PaymentAttempt = zod_1.z.object({
    /** Client-side ULID; the source of truth for idempotency. */
    attemptId: exports.PaymentAttemptId,
    shiftClientUuid: zod_1.z.string().min(1).max(64),
    terminalId: zod_1.z.string(),
    cashierId: zod_1.z.string(),
    method: exports.PaymentMethod,
    /**
     * Requested amount in the smallest currency unit (cents). Positive
     * for sales, negative for refunds (TRS §17.6 — refund-on-card path).
     */
    amountMinor: pricing_js_1.MoneyMinor,
    currencyCode: zod_1.z.string().length(3, 'ISO 4217 alpha-3'),
    /** Opaque receipt id this attempt is tied to (set once known). */
    receiptId: zod_1.z.string().nullable(),
    /** Vendor-side reference returned by the terminal SDK. */
    vendorAttemptRef: zod_1.z.string().nullable(),
    /** Terminal-asserted clock for ordering across batched uploads. */
    initiatedAt: zod_1.z.string().datetime(),
});
// ---------- PaymentResult — closes the lifecycle (or surfaces the indeterminate) ----------
exports.PaymentResult = zod_1.z.object({
    attemptId: exports.PaymentAttemptId,
    state: exports.PaymentState,
    /**
     * Vendor-supplied response code; opaque per TRS §17.7. Logged for
     * forensics; not parsed by BOS.
     */
    vendorResponseCode: zod_1.z.string().nullable(),
    /** Vendor's transaction id when the vendor returned one. */
    vendorTransactionId: zod_1.z.string().nullable(),
    /**
     * The amount the vendor confirms — usually equals
     * `attempt.amountMinor` but can differ in tip-on-card or partial-
     * auth flows (Sprint 9+; reserved here).
     */
    confirmedAmountMinor: pricing_js_1.MoneyMinor.nullable(),
    /** Terminal-asserted clock. */
    resolvedAt: zod_1.z.string().datetime(),
    /** Operator-readable reason on decline / failure. */
    reasonMessage: zod_1.z.string().nullable(),
});
// ---------- ReversalRequest — BOS-authored, terminal pulls ----------
exports.ReversalRequest = zod_1.z.object({
    attemptId: exports.PaymentAttemptId,
    /**
     * The reason BOS issued the reversal (e.g. `fiscal-void-failed`,
     * `recon-queue-manual`, `acquirer-bounce`).
     */
    reasonCode: zod_1.z.string().min(1).max(64),
    reasonMessage: zod_1.z.string().nullable(),
    /** BOS user id that authored the reversal (when manager-initiated). */
    authoredByUserId: zod_1.z.string().nullable(),
    authoredAt: zod_1.z.string().datetime(),
});
// ---------- BatchReport — acquirer end-of-day ----------
exports.BatchReport = zod_1.z.object({
    batchRef: zod_1.z.string(),
    acquirer: zod_1.z.string(),
    /** Terminals that contributed to the batch. */
    terminalIds: zod_1.z.array(zod_1.z.string()).min(1),
    /** Inclusive range. */
    fromIso: zod_1.z.string().datetime(),
    toIso: zod_1.z.string().datetime(),
    totalSalesMinor: pricing_js_1.MoneyMinor,
    totalRefundsMinor: pricing_js_1.MoneyMinor,
    netMinor: pricing_js_1.MoneyMinor,
    /** Attempts the acquirer settled vs. our local ledger; nonzero = recon needed. */
    discrepancyMinor: pricing_js_1.MoneyMinor,
});
// ---------- Acquirer batch reconciliation (Sprint 12 — TRS §17.6) ----------
/**
 * Per-record line from the acquirer's end-of-day file. Operators
 * import a parsed list of these via `POST /payments/acquirer-batches`;
 * BOS diffs against the local `payment_attempts` table on
 * `vendorAttemptRef` and surfaces discrepancies in the response.
 *
 * The wire shape stays narrow on purpose — adapters (PrivatBank,
 * Lithuanian banks, etc.) will eventually emit different file
 * formats; the BOS-side ingestor normalises to this shape before
 * calling the recon endpoint.
 */
exports.AcquirerBatchRecord = zod_1.z.object({
    vendorAttemptRef: zod_1.z.string().min(1).max(128),
    amountMinor: pricing_js_1.MoneyMinor,
    /** Optional richer vendor id (e.g. PrivatBank transaction id). */
    vendorTransactionId: zod_1.z.string().max(128).nullable().optional(),
    /** When the acquirer settled the row. */
    settledAt: zod_1.z.string().datetime(),
});
exports.AcquirerBatchUploadRequest = zod_1.z.object({
    /** Free-form vendor identifier (`privatbank`, `swedbank-lt`, …). */
    acquirer: zod_1.z.string().min(1).max(64),
    /** Operator-supplied batch identifier from the vendor file. */
    batchRef: zod_1.z.string().min(1).max(128),
    records: zod_1.z.array(exports.AcquirerBatchRecord).min(1).max(50_000),
});
exports.AcquirerBatchDiscrepancy = zod_1.z.object({
    /** Why this row is in the discrepancy list. */
    kind: zod_1.z.enum(['missing_from_bos', 'missing_from_vendor', 'amount_mismatch', 'state_mismatch']),
    vendorAttemptRef: zod_1.z.string(),
    bosAttemptId: zod_1.z.string().nullable(),
    /** Vendor-side amount when known. */
    vendorAmountMinor: pricing_js_1.MoneyMinor.nullable(),
    /** BOS-side amount when known. */
    bosAmountMinor: pricing_js_1.MoneyMinor.nullable(),
    bosState: exports.PaymentState.nullable(),
    note: zod_1.z.string().max(500),
});
exports.AcquirerBatchUploadResponse = zod_1.z.object({
    acquirer: zod_1.z.string(),
    batchRef: zod_1.z.string(),
    processedAt: zod_1.z.string().datetime(),
    matchedCount: zod_1.z.number().int().min(0),
    discrepancies: zod_1.z.array(exports.AcquirerBatchDiscrepancy),
    totalVendorMinor: pricing_js_1.MoneyMinor,
    totalBosMatchedMinor: pricing_js_1.MoneyMinor,
});
// ---------- Admin REST surface (Reconciliation queue) ----------
/**
 * Per-row shape returned by `GET /payments/reconciliation`. Stuck
 * attempts (`awaiting_response`, `indeterminate`) drive the
 * reconciliation queue UI per TRS §17.5.
 */
exports.PaymentAttemptSummary = zod_1.z.object({
    id: zod_1.z.string(),
    attemptId: exports.PaymentAttemptId,
    shiftId: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    cashierId: zod_1.z.string(),
    method: exports.PaymentMethod,
    amountMinor: pricing_js_1.MoneyMinor,
    currencyCode: zod_1.z.string().length(3),
    state: exports.PaymentState,
    vendorAttemptRef: zod_1.z.string().nullable(),
    vendorResponseCode: zod_1.z.string().nullable(),
    reasonMessage: zod_1.z.string().nullable(),
    initiatedAt: zod_1.z.string().datetime(),
    resolvedAt: zod_1.z.string().datetime().nullable(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.PaymentReconcileRequest = zod_1.z.object({
    /**
     * `mark_voided` — finalise an indeterminate attempt as voided
     * (no funds moved). `mark_approved` — vendor batch reconciliation
     * confirmed the attempt actually settled. `issue_reversal` — fund
     * movement happened but receipt failed; queue a ReversalRequest.
     */
    action: zod_1.z.enum(['mark_voided', 'mark_approved', 'issue_reversal']),
    reason: zod_1.z.string().min(1).max(1000),
});
//# sourceMappingURL=payment.js.map